import React, {useCallback, useEffect, useState} from 'react'
import {BE_BASE_URL} from '@/App'
import {Snackbar} from '@mui/material'
import Alert from '@mui/material/Alert'
import {useDispatch, useSelector} from 'react-redux'
import {InitialStateType} from '@/store/reducers'
import {getGuestTokenName, getUserTokenName} from '@/utils/tokenUtils'
import {SET_USER_AUTH_ERROR, SET_USER_LOGGED} from '@/store/types'
import Grid from '@mui/material/GridLegacy'

type AuthProviderProps = {
    children: React.ReactNode
}

const AuthProvider = ({children}: AuthProviderProps) => {
    const [loggedSnackOpen, setLoggedSnackOpen] = React.useState<boolean>(false)
    const [logoutSnackOpen, setLogoutSnackOpen] = React.useState<boolean>(false)
    const [loginLoading, setLoginLoading] = useState<boolean>(true)

    const {logged, type, authError} = useSelector((state: InitialStateType) => state.user)
    const dispatch = useDispatch()

    const guestLogin = () => {
        return fetch(BE_BASE_URL + '/auth/guest_token', {
            credentials: 'include',
            mode: 'cors'
        })
            .then(res => {
                if(res.ok) {
                    return res.json()
                }
                return Promise.reject(res)
            })
            .catch(err => {
                console.log(err)
            })
            
    }
    
    useEffect(() => {
        const token = sessionStorage.getItem(getUserTokenName())
        // user token does not exist, we log as guests
        if(!token) {
            guestLogin()
                .then(res => {
                    sessionStorage.setItem(getGuestTokenName(), res.value)
                    dispatch(SET_USER_LOGGED(true, 'guest'))
                })
                .finally(() => {
                    setLoginLoading(false)
                })
        } else {
            // keeping logged as false so snack bar does not open
            dispatch(SET_USER_LOGGED(false, 'user'))
            setLoginLoading(false)
        }
    }, [])

    useEffect(() => {
        // user token invalid, log guest user
        if(authError) {
            setLogoutSnackOpen(true)
            guestLogin()
                .then(res => {
                    sessionStorage.setItem(getGuestTokenName(), res.value)
                    dispatch(SET_USER_LOGGED(true, 'guest'))
                    sessionStorage.removeItem(getUserTokenName())
                    return dispatch(SET_USER_AUTH_ERROR(false))
                })
        }
    }, [authError])

    useEffect(() => {
        if(logged) {
            setLoggedSnackOpen(true)
        }
    }, [logged, type])

    const onSnackbarClose = useCallback(() => {
        setLoggedSnackOpen(false)
        setLogoutSnackOpen(false)
    }, [])

    const onLoggedSnackClose = useCallback(() => {
        setLoggedSnackOpen(false)
    }, [])

    const onLogoutSnackClose = useCallback(() => {
        setLogoutSnackOpen(false)
    }, [])

    return (
        <>
            {!loginLoading && children}
            <Snackbar open={loggedSnackOpen || logoutSnackOpen}
                autoHideDuration={4000}
                onClose={onSnackbarClose}
                anchorOrigin={{vertical:'bottom', horizontal: 'right'}}>
                <Grid container spacing={2}>
                    <Grid item paddingBottom={1}>
                        {loggedSnackOpen && <Alert
                            onClose={onLoggedSnackClose}
                            severity={type === 'user' ? 'success' : 'info'}
                            variant="filled"
                            sx={{ width: '100%' }}
                        >
                            {`You have been logged in as ${type}`}
                        </Alert>}
                    </Grid>
                    <Grid item>
                        {logoutSnackOpen && <Alert
                            onClose={onLogoutSnackClose}
                            severity="warning"
                            variant="filled"
                            sx={{ width: '100%' }}
                        >
                            You have been logged out as user
                        </Alert>}
                    </Grid>
                </Grid>
            </Snackbar>
        </>
    )
}

export default AuthProvider